import { collection, getDocs, query, orderBy, limit } from "firebase/firestore"
import { httpsCallable } from "firebase/functions"
import { db, functions } from "../firebase"

/**
 * Fetch all users from Firebase with Auth metadata
 * @returns {Promise<Array>} Array of user objects
 */
export const fetchAllUsers = async () => {
  try {
    const usersRef = collection(db, "users")
    const usersSnapshot = await getDocs(usersRef)

    const users = []
    usersSnapshot.forEach(doc => {
      const data = doc.data()

      // Try to get last login from multiple possible fields
      let lastLoginAt = null
      if (data.lastLoginAt) {
        lastLoginAt = data.lastLoginAt?.toDate()
      } else if (data.last_login) {
        lastLoginAt = data.last_login?.toDate()
      } else if (data.lastLogin) {
        lastLoginAt = data.lastLogin?.toDate()
      } else if (data.metadata?.lastSignInTime) {
        lastLoginAt = new Date(data.metadata.lastSignInTime)
      } else if (data.createdAt) {
        // Fallback: use createdAt as they must have logged in to create account
        lastLoginAt = data.createdAt?.toDate()
      }

      users.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        lastLoginAt,
      })
    })

    // Sort by most recent signup
    users.sort((a, b) => {
      const dateA = a.createdAt || new Date(0)
      const dateB = b.createdAt || new Date(0)
      return dateB - dateA
    })

    return users
  } catch (error) {
    console.error("Error fetching users:", error)
    throw error
  }
}

/**
 * Fetch recent user signups
 * @param {number} limitCount - Number of users to fetch
 * @returns {Promise<Array>} Array of recent user objects
 */
export const fetchRecentSignups = async (limitCount = 10) => {
  try {
    const usersRef = collection(db, "users")
    const recentQuery = query(usersRef, orderBy("createdAt", "desc"), limit(limitCount))
    const snapshot = await getDocs(recentQuery)

    const users = []
    snapshot.forEach(doc => {
      const data = doc.data()
      users.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      })
    })

    return users
  } catch (error) {
    console.error("Error fetching recent signups:", error)
    throw error
  }
}

/**
 * Fetch all bathrooms with user information
 * @returns {Promise<Array>} Array of bathroom objects
 */
export const fetchAllBathroomsAdmin = async () => {
  try {
    const locationsRef = collection(db, "locations_geohashed")
    const locationsSnapshot = await getDocs(locationsRef)

    const bathrooms = []
    locationsSnapshot.forEach(locationDoc => {
      const locationData = locationDoc.data()
      if (locationData.bathrooms && Array.isArray(locationData.bathrooms)) {
        locationData.bathrooms.forEach(bathroom => {
          bathrooms.push({
            ...bathroom,
            locationId: locationDoc.id,
            locationName: locationData.name,
            address: bathroom.address || locationData.address,
            latitude: bathroom.latitude || locationData.latitude,
            longitude: bathroom.longitude || locationData.longitude,
            createdAt: bathroom.createdAt?.toDate(),
            updatedAt: bathroom.updatedAt?.toDate(),
          })
        })
      }
    })

    // Sort by most recent
    bathrooms.sort((a, b) => {
      const dateA = a.createdAt || new Date(0)
      const dateB = b.createdAt || new Date(0)
      return dateB - dateA
    })

    return bathrooms
  } catch (error) {
    console.error("Error fetching bathrooms for admin:", error)
    throw error
  }
}

/**
 * Fetch all reviews across all bathrooms
 * Full review docs (with userName) live in the subcollection:
 * locations_geohashed/{locationId}/bathrooms/{bathroomId}/reviews
 * The embedded bathroom.reviews array only has rating summaries.
 * @returns {Promise<Array>} Array of review objects with bathroom info
 */
export const fetchAllReviews = async () => {
  try {
    const locationsRef = collection(db, "locations_geohashed")
    const locationsSnapshot = await getDocs(locationsRef)

    const reviewFetches = []

    locationsSnapshot.forEach(locationDoc => {
      const locationData = locationDoc.data()
      const bathrooms = locationData.bathrooms || []

      bathrooms.forEach(bathroom => {
        if (!bathroom?.id) return
        const hasReviews = bathroom.reviewCount > 0 || (Array.isArray(bathroom.reviews) && bathroom.reviews.length > 0)
        if (!hasReviews) return

        reviewFetches.push(
          getDocs(collection(db, "locations_geohashed", locationDoc.id, "bathrooms", bathroom.id, "reviews")).then(snap =>
            snap.docs.map(reviewDoc => {
              const data = reviewDoc.data()
              return {
                id: reviewDoc.id,
                ...data,
                bathroomId: bathroom.id,
                bathroomName: bathroom.name || data.bathroomName || locationData.name,
                locationId: locationDoc.id,
                timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : data.timestamp,
                userName: data.userName || "Anonymous",
              }
            }),
          ),
        )
      })
    })

    const reviews = (await Promise.all(reviewFetches)).flat()

    reviews.sort((a, b) => {
      const dateA = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp || 0)
      const dateB = b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp || 0)
      return dateB - dateA
    })

    return reviews
  } catch (error) {
    console.error("Error fetching all reviews:", error)
    throw error
  }
}

/**
 * Get dashboard statistics
 * @returns {Promise<Object>} Object with dashboard stats
 */
export const getDashboardStats = async () => {
  try {
    const [users, bathrooms, reviews] = await Promise.all([fetchAllUsers(), fetchAllBathroomsAdmin(), fetchAllReviews()])

    // Calculate stats
    const now = new Date()
    const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000)
    const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

    const newUsersToday = users.filter(u => u.createdAt && u.createdAt >= last24Hours).length
    const newUsersWeek = users.filter(u => u.createdAt && u.createdAt >= last7Days).length
    const newUsersMonth = users.filter(u => u.createdAt && u.createdAt >= last30Days).length

    const newBathroomsToday = bathrooms.filter(b => b.createdAt && b.createdAt >= last24Hours).length
    const newBathroomsWeek = bathrooms.filter(b => b.createdAt && b.createdAt >= last7Days).length
    const newBathroomsMonth = bathrooms.filter(b => b.createdAt && b.createdAt >= last30Days).length

    const newReviewsToday = reviews.filter(r => {
      const date = r.timestamp instanceof Date ? r.timestamp : new Date(r.timestamp || 0)
      return date >= last24Hours
    }).length

    const newReviewsWeek = reviews.filter(r => {
      const date = r.timestamp instanceof Date ? r.timestamp : new Date(r.timestamp || 0)
      return date >= last7Days
    }).length

    const newReviewsMonth = reviews.filter(r => {
      const date = r.timestamp instanceof Date ? r.timestamp : new Date(r.timestamp || 0)
      return date >= last30Days
    }).length

    // Get users with most contributions
    const userContributions = {}
    bathrooms.forEach(b => {
      if (b.userId) {
        userContributions[b.userId] = (userContributions[b.userId] || 0) + 1
      }
    })
    reviews.forEach(r => {
      if (r.userId) {
        userContributions[r.userId] = (userContributions[r.userId] || 0) + 1
      }
    })

    const topContributors = Object.entries(userContributions)
      .map(([userId, count]) => {
        const user = users.find(u => u.id === userId)
        return {
          userId,
          userName: user?.displayName || user?.email || "Unknown",
          contributions: count,
        }
      })
      .sort((a, b) => b.contributions - a.contributions)
      .slice(0, 10)

    return {
      totalUsers: users.length,
      totalBathrooms: bathrooms.length,
      totalReviews: reviews.length,
      newUsersToday,
      newUsersWeek,
      newUsersMonth,
      newBathroomsToday,
      newBathroomsWeek,
      newBathroomsMonth,
      newReviewsToday,
      newReviewsWeek,
      newReviewsMonth,
      topContributors,
      recentUsers: users.slice(0, 5),
      recentBathrooms: bathrooms.slice(0, 5),
      recentReviews: reviews.slice(0, 5),
    }
  } catch (error) {
    console.error("Error getting dashboard stats:", error)
    throw error
  }
}

/**
 * Reset a user's password (admin only)
 * @param {string} uid - User ID
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Result of the password reset
 */
export const resetUserPassword = async (uid, newPassword) => {
  try {
    const resetPasswordFunction = httpsCallable(functions, "resetUserPassword")
    const result = await resetPasswordFunction({ uid, newPassword })
    return result.data
  } catch (error) {
    console.error("Error resetting user password:", error)
    throw error
  }
}
